import { Link, useSearch } from "@tanstack/react-router"
import { H1 } from "@holigral/shared/ui/components"
import { Badge } from "@holigral/shared/ui/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@holigral/shared/ui/components/ui/table"
import { AssignmentStatus } from "./model"
import { useGetAssignmentsQuery } from "./api"
import { useT } from "@/i18n"
import { TablePagination } from "@/base"
import { useCurrentTenant } from "@/tenant"
import { formatDate } from "@/utils"

const PER_PAGE = 25

export default function EvaluationsPage() {
  const { t } = useT("assignment")
  const { tenantSlug } = useCurrentTenant()
  const { page = 1 } = useSearch({ strict: false })

  const { data, isLoading } = useGetAssignmentsQuery({
    status: AssignmentStatus.Evaluated,
    page,
    perPage: PER_PAGE,
  })

  const rows = data?.items ?? []

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
      <H1>{t("evaluations.title")}</H1>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("evaluations.respondent")}</TableHead>
            <TableHead>{t("evaluations.brief")}</TableHead>
            {/* NOTE: rows carry no evaluated/published timestamp yet, so
                the submission date is shown instead */}
            <TableHead>{t("evaluations.submitted")}</TableHead>
            <TableHead>
              <span className="sr-only">{t("table.actions")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((assignment) => (
            <TableRow key={assignment.id}>
              <TableCell>
                <Link
                  to="/t/$tenantSlug/evaluations/$assignmentId"
                  params={{ tenantSlug, assignmentId: assignment.id }}
                  className="inline-flex items-center gap-2 underline underline-offset-3 hover:text-foreground"
                >
                  {assignment.respondent.email ?? "—"}
                  {assignment.isSelf && (
                    <Badge variant="outline">{t("table.self")}</Badge>
                  )}
                </Link>
              </TableCell>
              <TableCell>{assignment.brief?.name ?? "—"}</TableCell>
              <TableCell>
                {assignment.submittedAt
                  ? formatDate("date", assignment.submittedAt)
                  : "—"}
              </TableCell>
              <TableCell className="text-right">
                <Link
                  to="/t/$tenantSlug/evaluations/$assignmentId"
                  params={{ tenantSlug, assignmentId: assignment.id }}
                  className="text-sm underline underline-offset-3 hover:text-foreground"
                >
                  {t("evaluations.view")}
                </Link>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && !isLoading && (
            <TableRow>
              <TableCell
                colSpan={4}
                className="py-8 text-center text-muted-foreground"
              >
                {t("evaluations.empty")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination total={data?.total ?? 0} perPage={PER_PAGE} />
    </div>
  )
}
